import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { FileText, Clock, TrendingUp, CheckCircle, XCircle } from "lucide-react";
import { useTranslation } from 'react-i18next';

export default function DashboardStats({ stats }) {
  const { t } = useTranslation();

  if (!stats) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 mb-6 sm:mb-8"
    >
      <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm relative overflow-hidden" data-testid="stat-total">
        <div className="absolute inset-0 opacity-50 mix-blend-multiply pointer-events-none bg-[url('https://grainy-gradients.vercel.app/noise.svg')]"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-slate-100 rounded-lg">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />
            </div>
            <div>
              <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.total_applications}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.totalApplications')}</p>
            </div>
          </div>
        </div>
      </Card>
      
      <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="stat-pending">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-700" />
          </div>
          <div>
            <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.pending}</p>
            <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.pendingReview')}</p>
          </div>
        </div>
      </Card>
      
      <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="stat-in-progress">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
          </div>
          <div>
            <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.in_progress}</p>
            <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.inProgress')}</p>
          </div>
        </div>
      </Card>
      
      <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="stat-approved">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
          </div>
          <div>
            <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.approved}</p>
            <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.approved')}</p>
          </div>
        </div>
      </Card>
      
      <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="stat-rejected">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg">
            <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-700" />
          </div>
          <div>
            <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.rejected}</p>
            <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.rejected')}</p>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
